import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import { estimateService } from '../../services/estimateService';
import { EstimateResponse } from '../../types/estimate';

interface EstimatePDFPreviewProps {
  estimate: EstimateResponse;
  onDownloadPDF: () => void;
}

const EstimatePDFPreview: React.FC<EstimatePDFPreviewProps> = ({
  estimate,
  onDownloadPDF
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePDFPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const blob = await estimateService.generatePDF(estimate.id);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error: any) {
      console.error('Failed to generate PDF preview:', error);
      setError(error.response?.data?.detail || 'Failed to generate PDF preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generatePDFPreview();
    
    // Cleanup URL when component unmounts
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [estimate.id]);

  const handleRefresh = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    generatePDFPreview();
  };

  const handleFullscreen = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            PDF Preview
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh Preview">
              <span>
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Open in New Tab">
              <span>
                <IconButton onClick={handleFullscreen} disabled={!pdfUrl || loading}>
                  <FullscreenIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={onDownloadPDF}
              disabled={loading}
              size="small"
            >
              Download
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={handleRefresh} size="small" sx={{ ml: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {pdfUrl && !loading && (
          <Box
            sx={{
              width: '100%',
              height: 600,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={`Estimate ${estimate.estimate_number} PDF Preview`}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EstimatePDFPreview;